import React, { useEffect, useState } from 'react';
import { Loader2, Search, ShoppingBag } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { getAdminOrders } from '../../config';

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const data = await getAdminOrders({
          page: String(page),
          limit: '20',
          ...(status ? { status } : {}),
          ...(query ? { q: query } : {}),
        });
        if (!active) return;
        setOrders(data.orders || []);
        setPages(data.pages || 1);
      } catch {
        if (active) setOrders([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [page, query, status]);

  return (
    <AdminLayout title="Commandes">
      <div className="space-y-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <label className="flex-1 relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(event) => {
                setPage(1);
                setQuery(event.target.value);
              }}
              placeholder="Reference, client, email, produit"
              className="w-full bg-white border border-slate-200 rounded-full pl-11 pr-4 py-3 text-sm text-primary placeholder-slate-400 outline-none focus:border-secondary"
            />
          </label>

          <select
            value={status}
            onChange={(event) => {
              setPage(1);
              setStatus(event.target.value);
            }}
            className="bg-white border border-slate-200 rounded-full px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
          >
            <option value="">Tous les statuts</option>
            <option value="completed">Terminees</option>
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-secondary animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-sm border border-slate-200 p-10 text-center">
            <ShoppingBag className="w-10 h-10 text-gray-600 mx-auto mb-4" />
            <p className="text-slate-600">Aucune commande trouvee.</p>
          </div>
        ) : (
          <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Reference</th>
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Client</th>
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Produit</th>
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Montant</th>
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Date</th>
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} className="border-b border-slate-200 last:border-0">
                      <td className="px-4 py-3 text-primary font-medium">{order.reference}</td>
                      <td className="px-4 py-3 text-slate-700">
                        {order.first_name} {order.last_name}
                        <div className="text-xs text-slate-500">{order.email}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{order.product_name || order.summary || '-'}</td>
                      <td className="px-4 py-3 text-white">{typeof order.total === 'number' ? `${order.total.toFixed(2)} EUR` : '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDate(order.created_at)}</td>
                      <td className="px-4 py-3 text-slate-700">{order.status || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            {Array.from({ length: pages }, (_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setPage(index + 1)}
                className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                  page === index + 1
                    ? 'bg-secondary text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-100 hover:text-white'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
